import { Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import { useState, MouseEvent, FC } from 'react';

import { postRequestAssignmentsHistory } from '../../../studentApp/api';
import {
  usePracticeLockStatus,
  useProcessedWorkStore,
} from '../../../studentApp/hooks';
import { useLayout } from '../../../studentApp/hooks/useLayout';
import { useProcessedBannerStore } from '../../../studentApp/hooks/useProcessedBannerStore';
import { useProcessedStudentAppModalStore } from '../../../studentApp/hooks/useStudentAppModalStore';
import { bannerStore } from '../../../studentApp/stores';
import { getGuid, getSicId } from '../../../studentApp/stores/globalState';
import {
  EXAMS,
  GAMES,
  INFO,
  ModalType,
  SETTINGS,
  STATISTICS,
  SWITCH_CLASS,
  studentAppModalStore,
} from '../../../studentApp/stores/studentAppModalStore';
import { userStore } from '../../../studentApp/stores/userStore';

export const STUDENT_CALENDAR_READY = 'studentCalendarReady';

export const HamburgerMenu: FC = () => {
  const currentWork = useProcessedWorkStore().currentWorkType;
  const practiceLocked = usePracticeLockStatus();
  const [otherClasses, allowExamsOnMobile, gameCredits] = bannerStore(state => [
    state.otherClasses,
    state.allowExamsOnMobile,
    state.gameCredits,
  ]);
  const setModal = studentAppModalStore(state => state.setCurrentModal);
  const { isMobile } = useLayout();
  const { showExamsMenu } = useProcessedStudentAppModalStore();
  const { showGamesMenu, showDisabledGamesMenu } = useProcessedBannerStore();
  const [userName] = userStore(state => [state.userName]);

  const playGameText = isMobile
    ? `Play Game (${gameCredits} credit${gameCredits === 1 ? '' : 's'})`
    : 'Play Game';

  const hideMobileExams = isMobile && !allowExamsOnMobile;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const disableStatistics =
    practiceLocked || currentWork === 'CORRECTIONS' || currentWork === 'NONE';

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const launchModal = (modal: ModalType) => {
    setModal(modal);
    handleClose();
  };

  const handleAssignmentHistory = (): void => {
    postRequestAssignmentsHistory();
    handleClose();
  };

  return (
    <div style={{ display: 'flex' }}>
      <Tooltip title="More options">
        <IconButton onClick={handleClick}>
          <img
            src="GMM-icons/GMM-icon-hamburger.svg"
            style={{ height: '30px' }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onKeyDown={handleKeyDown}
      >
        {otherClasses.length > 0 && (
          <MenuItem onClick={() => launchModal(SWITCH_CLASS)}>
            Switch Class
          </MenuItem>
        )}

        {showExamsMenu && !hideMobileExams && (
          <MenuItem onClick={() => launchModal(EXAMS)}>
            Exams & Follow-ups
          </MenuItem>
        )}

        {showGamesMenu && (
          <MenuItem onClick={() => launchModal(GAMES)}>{playGameText}</MenuItem>
        )}

        {showDisabledGamesMenu && <MenuItem disabled>{playGameText}</MenuItem>}

        <MenuItem onClick={() => launchModal(SETTINGS)}>Settings</MenuItem>

        <MenuItem
          disabled={disableStatistics}
          onClick={() => launchModal(STATISTICS)}
        >
          Statistics
        </MenuItem>

        <MenuItem onClick={() => launchModal(INFO)}>Your Info</MenuItem>

        <MenuItem onClick={() => handleOpenPercentCalendar(userName)}>
          Calendar
        </MenuItem>

        <MenuItem onClick={handleAssignmentHistory}>Work History</MenuItem>

        <MenuItem>
          <a
            href="https://sites.google.com/frontporch.com/gmm-student-tutorials/home"
            rel="noreferrer"
            target="_blank"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Tutorials
          </a>
        </MenuItem>

        <MenuItem
          onClick={() => {
            // Refresh the current page to log student out
            window.location.reload();
          }}
        >
          Logout
        </MenuItem>
      </Menu>
    </div>
  );
};

/**
 * We had a student brute force access other student calendars by changing the URL
 * methodically through sicIds, so now we'll stop posting credentials in the URL.
 *
 * Instead, when the calendar tab is opened, it sends a message back to this tab
 * to indicate it is ready to receive the credentials it needs to access the calendar
 * data from the server. Then this tab sends a message back with the credentials.
 *
 * Note: when I just opened the calendar tab and sent it a message immediately from
 * this tab, we had race conditions -- it worked out on Chrome, but not on Safari.
 */
const handleOpenPercentCalendar = (userName: string): void => {
  const win = window.open('studentCalendar.html', '_blank');

  if (!win) return;

  const handleReadyMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      console.warn('Ignored message from untrusted origin:', event.origin);

      return;
    }

    if (event.data === STUDENT_CALENDAR_READY) {
      win.postMessage(
        {
          guid: getGuid(),
          studentInClassId: getSicId(),
          username: userName,
        },
        window.location.origin
      );
      window.removeEventListener('message', handleReadyMessage);
    }
  };

  // Listen for the "ready" message from the calendar tab
  window.addEventListener('message', handleReadyMessage);

  win.focus();
};
